import {
  Page,
  Layout,
  Card,
  TextField,
  Select,
  Checkbox,
  BlockStack,
  InlineStack,
  Text,
  RangeSlider,
  FormLayout,
  Button,
  Modal,
  Tabs,
  InlineGrid,
  Divider,
  ChoiceList,
  RadioButton,
  Spinner,
  ColorPicker,
  Popover,
  ActionList,
  Tag,
  Thumbnail,
  Banner,
} from '@shopify/polaris';
import {
  DeleteMinor,
  CirclePlusMinor,
} from '@shopify/polaris-icons';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { createApiUrl } from '../config/api';
import { useApiClient, ApiError } from '../hooks/useApiClient';
import { useAppNavigate } from '../hooks/useAppNavigate';

/* ================================================================
   TYPE DEFINITIONS
   ================================================================ */

interface StyleData {
  backgroundColor: string;
  textColor: string;
  fontSize: number;
  fontFamily: string;
  borderRadius: number;
  padding: string;
  fontWeight: string;
  fontStyle: string;
  textDecoration: string;
  width: number | null;
  height: number | null;
  lockAspectRatio: boolean;
  sizePreset: string;
  customFont: boolean;
  paddingTop: number;
  paddingBottom: number;
  paddingLeft: number;
  paddingRight: number;
}

interface Condition {
  type: string;
  operator: string;
  value: any;
  values: any[];
}

interface ConditionsData {
  products: string[];
  collections: string[];
  tags: string[];
  minPrice?: number;
  maxPrice?: number;
  stockStatus: string;
  vendors?: string[];
  productTypes?: string[];
  advancedConditions: Condition[];
}

interface VisibilityData {
  homePage: boolean;
  productPages: boolean;
  searchResultsPages: boolean;
  cartPage: boolean;
  collectionPages: boolean;
  otherPages: boolean;
}

interface DisplayPeriodData {
  allTime: boolean;
  startDate: string;
  endDate: string;
}

interface DaySchedule {
  enabled: boolean;
  allDay: boolean;
  startTime: string;
  endTime: string;
}

interface WeekScheduleData {
  timezone: string;
  days: {
    sunday: DaySchedule;
    monday: DaySchedule;
    tuesday: DaySchedule;
    wednesday: DaySchedule;
    thursday: DaySchedule;
    friday: DaySchedule;
    saturday: DaySchedule;
  };
}

interface PositionData {
  corner: string;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  marginUnit: string;
  customCSSSelector: string;
}

interface LabelData {
  id?: string; // Optional: present when editing existing label
  name: string;
  text: string;
  type: 'text' | 'image' | 'badge';
  enabled: boolean;
  position: string;
  shape: string;
  animation: string;
  matchType: string;
  linkUrl: string;
  linkNewWindow: boolean;
  rotation: number;
  imageUrl: string;
  style: StyleData;
  conditions: ConditionsData;
  visibility: VisibilityData;
  displayPeriod: DisplayPeriodData;
  weekSchedule: WeekScheduleData;
  translations: Record<string, { labelText: string }>;
  advancedPosition: PositionData;
}

/* ================================================================
   CONSTANTS
   ================================================================ */

const DEFAULT_DAY: DaySchedule = { enabled: true, allDay: true, startTime: '8:00 AM', endTime: '5:00 PM' };

const initialFormData: LabelData = {
  name: '',
  text: '',
  type: 'text',
  enabled: true,
  position: 'top-left',
  shape: 'rectangle',
  animation: 'none',
  matchType: 'any',
  linkUrl: '',
  linkNewWindow: false,
  rotation: 0,
  imageUrl: '',
  style: {
    backgroundColor: '#ff0000',
    textColor: '#ffffff',
    fontSize: 14,
    fontFamily: 'Arial, sans-serif',
    borderRadius: 4,
    padding: '8px 12px',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none',
    width: null,
    height: null,
    lockAspectRatio: false,
    sizePreset: 'medium',
    customFont: false,
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 12,
    paddingRight: 12,
  },
  conditions: {
    products: [],
    collections: [],
    tags: [],
    stockStatus: 'all',
    advancedConditions: [],
  },
  visibility: {
    homePage: true,
    productPages: true,
    searchResultsPages: true,
    cartPage: true,
    collectionPages: true,
    otherPages: true,
  },
  displayPeriod: {
    allTime: true,
    startDate: '',
    endDate: '',
  },
  weekSchedule: {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    days: {
      sunday: { ...DEFAULT_DAY },
      monday: { ...DEFAULT_DAY },
      tuesday: { ...DEFAULT_DAY },
      wednesday: { ...DEFAULT_DAY },
      thursday: { ...DEFAULT_DAY },
      friday: { ...DEFAULT_DAY },
      saturday: { ...DEFAULT_DAY },
    },
  },
  translations: {},
  advancedPosition: {
    corner: 'top-left',
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginUnit: 'px',
    customCSSSelector: '',
  },
};

const SHAPE_OPTIONS = [
  { label: 'Square', value: 'square' },
  { label: 'Rectangle', value: 'rectangle' },
  { label: 'Circle', value: 'circle' },
  { label: 'Rounded', value: 'rounded' },
  { label: 'Pill', value: 'pill' },
];

const ANIMATION_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'Fade', value: 'fade' },
  { label: 'Pulse', value: 'pulse' },
  { label: 'Bounce In', value: 'bounce_in' },
  { label: 'Bounce Out', value: 'bounce_out' },
  { label: 'Swing', value: 'swing' },
  { label: 'Hi There', value: 'hi_there' },
  { label: 'Flash', value: 'flash' },
  { label: 'Roll In', value: 'roll_in' },
  { label: 'Roll Out', value: 'roll_out' },
];

const POSITION_GRID = [
  ['top-left', 'top-center', 'top-right'],
  ['middle-left', 'center', 'middle-right'],
  ['bottom-left', 'bottom-center', 'bottom-right'],
];

const CONDITION_TYPES = [
  { label: 'Products selected', value: 'products_selected' },
  { label: 'Collections selected', value: 'collections_selected' },
  { label: 'Product tag', value: 'product_tag' },
  { label: 'Product created', value: 'product_created' },
  { label: 'Product title', value: 'product_title' },
  { label: 'Product type', value: 'product_type' },
  { label: 'Product vendor', value: 'product_vendor' },
  { label: 'Product price', value: 'product_price' },
  { label: 'Product compare-at price', value: 'product_compare_at_price' },
  { label: 'Product weight', value: 'product_weight' },
  { label: 'Product published', value: 'product_published' },
  { label: 'Product inventory', value: 'product_inventory' },
  { label: 'Product variants', value: 'product_variants' },
  { label: 'Visitor country', value: 'visitor_country' },
  { label: 'Available for sale', value: 'available_for_sale' },
];

const FONT_OPTIONS = [
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' },
  { label: 'Courier New', value: '"Courier New", monospace' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Impact', value: 'Impact, sans-serif' },
  { label: 'Comic Sans MS', value: '"Comic Sans MS", cursive' },
  { label: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
  { label: 'Lucida Console', value: '"Lucida Console", monospace' },
];

const TIMEZONE_OPTIONS = [
  { label: 'America/New_York (EST)', value: 'America/New_York' },
  { label: 'America/Chicago (CST)', value: 'America/Chicago' },
  { label: 'America/Denver (MST)', value: 'America/Denver' },
  { label: 'America/Los_Angeles (PST)', value: 'America/Los_Angeles' },
  { label: 'Europe/London (GMT)', value: 'Europe/London' },
  { label: 'Europe/Berlin (CET)', value: 'Europe/Berlin' },
  { label: 'Europe/Paris (CET)', value: 'Europe/Paris' },
  { label: 'Asia/Tokyo (JST)', value: 'Asia/Tokyo' },
  { label: 'Asia/Shanghai (CST)', value: 'Asia/Shanghai' },
  { label: 'Asia/Kolkata (IST)', value: 'Asia/Kolkata' },
  { label: 'Asia/Dubai (GST)', value: 'Asia/Dubai' },
  { label: 'Australia/Sydney (AEST)', value: 'Australia/Sydney' },
  { label: 'Pacific/Auckland (NZST)', value: 'Pacific/Auckland' },
];

const TIME_OPTIONS = (() => {
  const times: { label: string; value: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const hr = h % 12 || 12;
      const ampm = h < 12 ? 'AM' : 'PM';
      const label = `${hr}:${m.toString().padStart(2, '0')} ${ampm}`;
      times.push({ label, value: label });
    }
  }
  return times;
})();

/** Returns true if the condition has a type and is not an empty placeholder (used for display and save). */
function isConditionValid(cond: Condition | null | undefined): cond is Condition {
  if (!cond || typeof cond !== 'object' || !cond.type) return false;
  // For product/collection selectors, allow empty values (user may not have selected yet)
  return true;
}

/** Filters out null, empty, or invalid condition objects. Use when loading and saving. */
function sanitizeAdvancedConditions(conditions: any[]): Condition[] {
  if (!Array.isArray(conditions)) return [];
  return conditions.filter((c): c is Condition => isConditionValid(c));
}

const COUNTRY_OPTIONS = [
  { label: 'United States', value: 'US' },
  { label: 'Canada', value: 'CA' },
  { label: 'United Kingdom', value: 'GB' },
  { label: 'Germany', value: 'DE' },
  { label: 'France', value: 'FR' },
  { label: 'Spain', value: 'ES' },
  { label: 'Italy', value: 'IT' },
  { label: 'Netherlands', value: 'NL' },
  { label: 'Australia', value: 'AU' },
  { label: 'Japan', value: 'JP' },
  { label: 'South Korea', value: 'KR' },
  { label: 'China', value: 'CN' },
  { label: 'India', value: 'IN' },
  { label: 'Brazil', value: 'BR' },
  { label: 'Mexico', value: 'MX' },
  { label: 'Russia', value: 'RU' },
  { label: 'Pakistan', value: 'PK' },
  { label: 'UAE', value: 'AE' },
  { label: 'Saudi Arabia', value: 'SA' },
  { label: 'South Africa', value: 'ZA' },
];

/* ================================================================
   MAIN COMPONENT
   ================================================================ */

export default function LabelEditor() {
  const location = useLocation();
  const navigate = useAppNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState<LabelData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [conditionPopoverActive, setConditionPopoverActive] = useState(false);
  const [availableCollections, setAvailableCollections] = useState<any[]>([]);
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [availableLocales, setAvailableLocales] = useState<any[]>([]);
  const [conditionsLoadError, setConditionsLoadError] = useState<string | null>(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [textColorPickerOpen, setTextColorPickerOpen] = useState(false);
  // Product picker modal state
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerConditionIdx, setProductPickerConditionIdx] = useState<number>(-1);
  const [productPickerSelected, setProductPickerSelected] = useState<string[]>([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  // Collection picker modal state
  const [collectionPickerOpen, setCollectionPickerOpen] = useState(false);
  const [collectionPickerConditionIdx, setCollectionPickerConditionIdx] = useState<number>(-1);
  const [collectionPickerSelected, setCollectionPickerSelected] = useState<string[]>([]);
  const [collectionSearchQuery, setCollectionSearchQuery] = useState('');
  const api = useApiClient();

  // ── Load label data if editing ──
  useEffect(() => {
    if (id) {
      fetchLabel(id);
    } else if (location.state?.template) {
      const tpl = location.state.template;
      setFormData((prev) => ({ ...prev, ...tpl }));
    }
  }, [id, location.state]);

  // ── Fetch collections, products, locales on mount ──
  useEffect(() => {
    fetchCollections();
    fetchProducts();
    fetchLocales();
  }, []);

  const fetchLabel = async (labelId: string) => {
    setLoading(true);
    try {
      console.log(`[LabelEditor] Fetching label with ID: ${labelId}`);
      const data = await api.get<any>(createApiUrl(`api/labels/${labelId}`));
      console.log(`[LabelEditor] Label fetched successfully:`, { id: data.id, name: data.name, shopId: data.shopId });
      const rawConditions = data.conditions || {};
      const advancedConditions = sanitizeAdvancedConditions(rawConditions.advancedConditions || []);
      // Merge fetched data; normalize conditions so we only have valid advanced conditions
      // IMPORTANT: Use the ID from the fetched data, not from URL params, to ensure consistency
      setFormData((prev) => ({
        ...prev,
        ...data,
        id: data.id, // Ensure we use the database ID
        style: { ...prev.style, ...(data.style || {}) },
        conditions: {
          ...prev.conditions,
          ...rawConditions,
          advancedConditions,
          tags: rawConditions.tags || [],
          products: rawConditions.products || [],
          collections: rawConditions.collections || [],
        },
        visibility: { ...prev.visibility, ...(data.visibility || {}) },
        displayPeriod: { ...prev.displayPeriod, ...(data.displayPeriod || {}) },
        weekSchedule: {
          ...prev.weekSchedule,
          ...(data.weekSchedule || {}),
          days: {
            ...prev.weekSchedule.days,
            ...(data.weekSchedule?.days || {}),
          },
        },
        translations: data.translations || {},
        advancedPosition: { ...prev.advancedPosition, ...(data.advancedPosition || {}) },
      }));
    } catch (error) {
      console.error('[LabelEditor] Error fetching label:', error);
      if (error instanceof ApiError) {
        if (error.status === 404) {
          alert('Label not found. It may have been deleted.');
          navigate('/labels');
        } else if (error.status === 403) {
          alert('You do not have permission to access this label.');
          navigate('/labels');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = async () => {
    try {
      const data = await api.get<any[]>(createApiUrl('api/collections'));
      setAvailableCollections(data || []);
    } catch (e: any) {
      console.error('Error fetching collections:', e);
      if (e instanceof ApiError && e.code === 'TOKEN_REVOKED') {
        // useApiClient will handle the redirect — show message here too
        setConditionsLoadError('App session has expired. The page will redirect you to reinstall the app.');
      } else {
        setConditionsLoadError(
          `Could not load collections (${e?.status || 'error'}): ${e?.message || 'Unknown error'}. Try refreshing the page.`
        );
      }
    }
  };

  const fetchProducts = async () => {
    try {
      const data = await api.get<any[]>(createApiUrl('api/products'));
      setAvailableProducts(data || []);
    } catch (e: any) {
      console.error('Error fetching products:', e);
      if (e instanceof ApiError && e.code === 'TOKEN_REVOKED') {
        setConditionsLoadError('App session has expired. The page will redirect you to reinstall the app.');
      } else {
        setConditionsLoadError(
          `Could not load products (${e?.status || 'error'}): ${e?.message || 'Unknown error'}. Try refreshing the page.`
        );
      }
    }
  };

  const fetchLocales = async () => {
    try {
      const data = await api.get<any>(createApiUrl('api/locales'));
      setAvailableLocales(data?.availableLocales || []);
    } catch (e) {
      console.error('Error fetching locales:', e);
    }
  };

  const searchProducts = useCallback(async (query: string) => {
    setProductSearchLoading(true);
    try {
      const url = query
        ? createApiUrl(`api/products?search=${encodeURIComponent(query)}`)
        : createApiUrl('api/products');
      const data = await api.get<any[]>(url);
      setAvailableProducts(data || []);
    } catch (e) {
      console.error('Error searching products:', e);
    } finally {
      setProductSearchLoading(false);
    }
  }, [api]);

  // Open product picker for a specific condition
  const openProductPicker = (conditionIdx: number) => {
    const cond = formData.conditions.advancedConditions[conditionIdx];
    setProductPickerConditionIdx(conditionIdx);
    setProductPickerSelected(cond?.values || []);
    setProductSearchQuery('');
    setProductPickerOpen(true);
  };

  // Confirm product picker selection
  const confirmProductPicker = () => {
    if (productPickerConditionIdx >= 0) {
      updateCondition(productPickerConditionIdx, 'values', productPickerSelected);
    }
    setProductPickerOpen(false);
  };

  // Toggle a product in the picker
  const toggleProductInPicker = (productId: string) => {
    setProductPickerSelected((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  // Get product info by id from available products
  const getProductById = (productId: string) => {
    return availableProducts.find(
      (p) => String(p.id) === String(productId) || p.handle === productId
    );
  };

  const openCollectionPicker = (conditionIdx: number) => {
    const cond = formData.conditions.advancedConditions[conditionIdx];
    setCollectionPickerConditionIdx(conditionIdx);
    setCollectionPickerSelected(cond?.values || []);
    setCollectionSearchQuery('');
    setCollectionPickerOpen(true);
  };

  const confirmCollectionPicker = () => {
    if (collectionPickerConditionIdx >= 0) {
      updateCondition(collectionPickerConditionIdx, 'values', collectionPickerSelected);
    }
    setCollectionPickerOpen(false);
  };

  const toggleCollectionInPicker = (collectionId: string) => {
    setCollectionPickerSelected((prev) =>
      prev.includes(collectionId)
        ? prev.filter((id) => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const getCollectionById = (collectionId: string) => {
    return availableCollections.find((c) => String(c.id) === String(collectionId));
  };

  /* ── SAVE HANDLER ── */
  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a label name');
      return;
    }
    setSaving(true);
    try {
      const sanitizedAdvanced = sanitizeAdvancedConditions(formData.conditions.advancedConditions || []);
      const conditionsPayload = {
        ...formData.conditions,
        advancedConditions: sanitizedAdvanced,
        tags: [],
        products: [],
        collections: [],
      };

      const payload: any = {
        name: formData.name,
        text: formData.text,
        type: formData.type,
        enabled: formData.enabled,
        position: formData.advancedPosition.corner || formData.position,
        shape: formData.shape,
        animation: formData.animation,
        matchType: formData.matchType,
        linkUrl: formData.linkUrl || null,
        linkNewWindow: formData.linkNewWindow,
        rotation: formData.rotation,
        imageUrl: formData.imageUrl || null,
        style: formData.style,
        conditions: conditionsPayload,
        visibility: formData.visibility,
        displayPeriod: formData.displayPeriod,
        weekSchedule: formData.weekSchedule,
        translations: formData.translations,
        customCss: formData.advancedPosition.customCSSSelector
          ? `/* Custom selector: ${formData.advancedPosition.customCSSSelector} */`
          : null,
      };

      // Use formData.id if available (from fetched data), otherwise fall back to URL param id
      const labelId = formData.id || id;
      
      if (labelId) {
        console.log(`[LabelEditor] Updating label with ID: ${labelId}`);
        await api.put(createApiUrl(`api/labels/${labelId}`), payload);
        console.log(`[LabelEditor] Label updated successfully`);
      } else {
        console.log(`[LabelEditor] Creating new label`);
        await api.post(createApiUrl('api/labels'), payload);
        console.log(`[LabelEditor] Label created successfully`);
      }
      navigate('/labels');
    } catch (error) {
      console.error('[LabelEditor] Error saving label:', error);
      if (error instanceof ApiError) {
        if (error.status === 404) {
          alert(`Label not found. It may have been deleted. Please refresh the page and try again.`);
        } else if (error.status === 403) {
          alert(`You do not have permission to update this label. It may belong to a different shop.`);
        } else {
          alert(`Error saving label: ${error.message || 'Unknown error'}`);
        }
      } else {
        alert(`Error saving label: ${(error as any)?.message || 'Unknown error'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  /* ── Field update helpers ── */
  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateStyleField = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      style: { ...prev.style, [field]: value },
    }));
  };

  const updateVisibility = (field: string, value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      visibility: { ...prev.visibility, [field]: value },
    }));
  };

  const updateDisplayPeriod = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      displayPeriod: { ...prev.displayPeriod, [field]: value },
    }));
  };

  const updateWeekDay = (day: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      weekSchedule: {
        ...prev.weekSchedule,
        days: {
          ...prev.weekSchedule.days,
          [day]: { ...(prev.weekSchedule.days as any)[day], [field]: value },
        },
      },
    }));
  };

  const updatePosition = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      advancedPosition: { ...prev.advancedPosition, [field]: value },
    }));
  };

  /* ── Add a display condition ── */
  const addCondition = (type: string) => {
    const newCondition: Condition = {
      type,
      operator: 'is_equal',
      value: '',
      values: [],
    };
    setFormData((prev) => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        advancedConditions: [...prev.conditions.advancedConditions, newCondition],
      },
    }));
    setConditionPopoverActive(false);
  };

  const updateCondition = (index: number, field: string, value: any) => {
    setFormData((prev) => {
      const updated = [...prev.conditions.advancedConditions];
      updated[index] = { ...updated[index], [field]: value };
      return {
        ...prev,
        conditions: { ...prev.conditions, advancedConditions: updated },
      };
    });
  };

  const removeCondition = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        advancedConditions: prev.conditions.advancedConditions.filter((_, i) => i !== index),
      },
    }));
  };

  /* ── Translation helpers ── */
  const updateTranslation = (locale: string, text: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [locale]: { labelText: text },
      },
    }));
  };

  /* ── Hex to HSB / HSB to Hex converters for ColorPicker ── */
  const hexToHsb = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    return { hue: h * 360, saturation: max === 0 ? 0 : d / max, brightness: max };
  };

  const hsbToHex = (hsb: { hue: number; saturation: number; brightness: number }) => {
    const h = hsb.hue / 360;
    const s = hsb.saturation;
    const v = hsb.brightness;
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);
    let r = 0, g = 0, b = 0;
    switch (i % 6) {
      case 0: r = v; g = t; b = p; break;
      case 1: r = q; g = v; b = p; break;
      case 2: r = p; g = v; b = t; break;
      case 3: r = p; g = q; b = v; break;
      case 4: r = t; g = p; b = v; break;
      case 5: r = v; g = p; b = q; break;
    }
    const toHex = (n: number) => Math.round(n * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  /* ── Editor Tabs ── */
  const tabs = [
    { id: 'label', content: 'Label' },
    { id: 'styles', content: 'Styles' },
    { id: 'position', content: 'Position' },
    { id: 'conditions', content: 'Conditions' },
    { id: 'visibility', content: 'Visibility' },
    { id: 'schedule', content: 'Schedule' },
    { id: 'link', content: 'Link' },
    { id: 'animations', content: 'Animations' },
  ];

  if (loading) {
    return (
      <Page title="Loading...">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  /* ================================================================
     RENDER: LABEL TAB (Type, Text, Image, Locales)
     ================================================================ */
  const renderLabelTab = () => (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">Label</Text>
        <FormLayout>
          <TextField
            label="Label Name"
            value={formData.name}
            onChange={(v) => updateField('name', v)}
            autoComplete="off"
            placeholder="e.g., Sale Label"
            helpText="Internal name for identification"
          />

          {/* Type selection: Image or Text */}
          <BlockStack gap="200">
            <Text as="p" variant="bodyMd" fontWeight="semibold">Type</Text>
            <Text as="p" variant="bodySm" tone="subdued">Choose the type of your label</Text>
            <InlineStack gap="300">
              <div
                onClick={() => updateField('type', 'image')}
                style={{
                  border: formData.type === 'image' ? '2px solid #2C6ECB' : '1px solid #ccc',
                  borderRadius: 8, padding: '16px 24px', cursor: 'pointer', textAlign: 'center',
                  backgroundColor: formData.type === 'image' ? '#F1F8FF' : '#fff',
                }}
              >
                <div style={{ fontSize: 24, marginBottom: 4 }}>🖼️</div>
                <Text as="p" variant="bodySm">Image</Text>
              </div>
              <div
                onClick={() => updateField('type', 'text')}
                style={{
                  border: formData.type === 'text' ? '2px solid #2C6ECB' : '1px solid #ccc',
                  borderRadius: 8, padding: '16px 24px', cursor: 'pointer', textAlign: 'center',
                  backgroundColor: formData.type === 'text' ? '#F1F8FF' : '#fff',
                }}
              >
                <div style={{ fontSize: 24, marginBottom: 4 }}>A</div>
                <Text as="p" variant="bodySm">Text</Text>
              </div>
            </InlineStack>
          </BlockStack>

          {formData.type === 'text' ? (
            <BlockStack gap="200">
              <InlineStack align="space-between">
                <Text as="p" variant="bodyMd" fontWeight="semibold">Enter the text for label</Text>
                <Button variant="plain" onClick={() => setTranslationModalOpen(true)}>
                  Locales
                </Button>
              </InlineStack>
              <TextField
                label=""
                labelHidden
                value={formData.text}
                onChange={(v) => updateField('text', v)}
                autoComplete="off"
                placeholder="e.g., Extra $30 Off (applied)"
              />
            </BlockStack>
          ) : (
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd" fontWeight="semibold">Label Image</Text>
              <TextField
                label="Image URL"
                labelHidden
                value={formData.imageUrl || ''}
                onChange={(v) => updateField('imageUrl', v)}
                autoComplete="off"
                placeholder="https://example.com/label-image.png"
                helpText="Enter the URL of your label image (PNG, JPG, SVG, GIF)"
              />
              {formData.imageUrl && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={formData.imageUrl}
                    alt="Label preview"
                    style={{ maxWidth: 150, maxHeight: 60, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 4 }}
                  />
                </div>
              )}
            </BlockStack>
          )}

          <Checkbox
            label="Enable Label"
            checked={formData.enabled}
            onChange={(v) => updateField('enabled', v)}
          />
        </FormLayout>
      </BlockStack>
    </Card>
  );

  /* ================================================================
     RENDER: STYLES TAB (Shape, Colors, Font, Size, Spacing, Rotation)
     ================================================================ */
  const renderStylesTab = () => (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">Styles</Text>

        {/* Shape */}
        <Select
          label="Shape"
          options={SHAPE_OPTIONS}
          value={formData.shape}
          onChange={(v) => updateField('shape', v)}
        />

        {/* Background Color */}
        <BlockStack gap="200">
          <Text as="p" variant="bodyMd" fontWeight="semibold">Background</Text>
          <InlineStack gap="200" blockAlign="center">
            <div
              style={{
                width: 32, height: 32, borderRadius: '50%', cursor: 'pointer',
                backgroundColor: formData.style.backgroundColor, border: '2px solid #ddd',
              }}
              onClick={() => setColorPickerOpen(!colorPickerOpen)}
            />
            <TextField
              label="" labelHidden
              value={formData.style.backgroundColor}
              onChange={(v) => updateStyleField('backgroundColor', v)}
              autoComplete="off"
            />
          </InlineStack>
          {colorPickerOpen && (
            <ColorPicker
              onChange={(c) => updateStyleField('backgroundColor', hsbToHex(c))}
              color={hexToHsb(formData.style.backgroundColor)}
            />
          )}
        </BlockStack>

        {/* Corner Radius */}
        <RangeSlider
          label="Corner radius"
          value={formData.style.borderRadius}
          onChange={(v) => updateStyleField('borderRadius', v)}
          min={0} max={50} output suffix="px"
        />

        <Divider />

        {/* Font */}
        <BlockStack gap="300">
          <Text as="p" variant="headingSm">Font</Text>
          <Select
            label="Font Family"
            options={FONT_OPTIONS}
            value={formData.style.fontFamily}
            onChange={(v) => updateStyleField('fontFamily', v)}
          />
          <Checkbox
            label="Use custom font"
            checked={formData.style.customFont}
            onChange={(v) => updateStyleField('customFont', v)}
          />
          {formData.style.customFont && (
            <TextField
              label="Custom Font Family"
              value={formData.style.fontFamily}
              onChange={(v) => updateStyleField('fontFamily', v)}
              autoComplete="off"
              placeholder="Enter custom font family name"
            />
          )}
          <RangeSlider
            label="Size"
            value={formData.style.fontSize}
            onChange={(v) => updateStyleField('fontSize', v)}
            min={8} max={48} output suffix="px"
          />

          {/* Font Style Buttons */}
          <InlineStack gap="200">
            <Button
              pressed={formData.style.fontWeight === 'bold'}
              onClick={() => updateStyleField('fontWeight', formData.style.fontWeight === 'bold' ? 'normal' : 'bold')}
            >
              B
            </Button>
            <Button
              pressed={formData.style.fontStyle === 'italic'}
              onClick={() => updateStyleField('fontStyle', formData.style.fontStyle === 'italic' ? 'normal' : 'italic')}
            >
              I
            </Button>
            <Button
              pressed={formData.style.textDecoration === 'underline'}
              onClick={() => updateStyleField('textDecoration', formData.style.textDecoration === 'underline' ? 'none' : 'underline')}
            >
              U
            </Button>
            <Button
              pressed={formData.style.textDecoration === 'line-through'}
              onClick={() => updateStyleField('textDecoration', formData.style.textDecoration === 'line-through' ? 'none' : 'line-through')}
            >
              S
            </Button>
          </InlineStack>

          {/* Text Color */}
          <BlockStack gap="200">
            <Text as="p" variant="bodySm">Text Color</Text>
            <InlineStack gap="200" blockAlign="center">
              <div
                style={{
                  width: 28, height: 28, borderRadius: '50%', cursor: 'pointer',
                  backgroundColor: formData.style.textColor, border: '2px dashed #aaa',
                }}
                onClick={() => setTextColorPickerOpen(!textColorPickerOpen)}
              />
              <TextField
                label="" labelHidden
                value={formData.style.textColor}
                onChange={(v) => updateStyleField('textColor', v)}
                autoComplete="off"
              />
            </InlineStack>
            {textColorPickerOpen && (
              <ColorPicker
                onChange={(c) => updateStyleField('textColor', hsbToHex(c))}
                color={hexToHsb(formData.style.textColor)}
              />
            )}
          </BlockStack>
        </BlockStack>

        <Divider />

        {/* Size Presets */}
        <BlockStack gap="300">
          <Text as="p" variant="headingSm">Size</Text>
          <InlineStack gap="200">
            {['small', 'medium', 'large'].map((preset) => (
              <Button
                key={preset}
                pressed={formData.style.sizePreset === preset}
                onClick={() => {
                  updateStyleField('sizePreset', preset);
                  if (preset === 'small') { updateStyleField('width', 50); updateStyleField('height', null); }
                  if (preset === 'medium') { updateStyleField('width', 75); updateStyleField('height', null); }
                  if (preset === 'large') { updateStyleField('width', 100); updateStyleField('height', null); }
                }}
              >
                {preset.charAt(0).toUpperCase() + preset.slice(1)}
              </Button>
            ))}
          </InlineStack>
          <InlineStack gap="300">
            <TextField
              label="Width"
              type="number"
              value={formData.style.width?.toString() || ''}
              onChange={(v) => updateStyleField('width', v ? parseInt(v) : null)}
              autoComplete="off" suffix="px"
            />
            <TextField
              label="Height"
              type="number"
              value={formData.style.height?.toString() || ''}
              onChange={(v) => updateStyleField('height', v ? parseInt(v) : null)}
              autoComplete="off" suffix="px"
            />
          </InlineStack>
          <Checkbox
            label="Lock aspect ratio"
            checked={formData.style.lockAspectRatio}
            onChange={(v) => updateStyleField('lockAspectRatio', v)}
          />
        </BlockStack>

        <Divider />

        {/* Spacing / Padding */}
        <BlockStack gap="300">
          <Text as="p" variant="headingSm">Spacing</Text>
          <Text as="p" variant="bodySm" tone="subdued">Padding</Text>
          <InlineGrid columns={2} gap="200">
            <TextField label="Top" type="number" value={String(formData.style.paddingTop)} onChange={(v) => updateStyleField('paddingTop', parseInt(v) || 0)} autoComplete="off" suffix="px" />
            <TextField label="Bottom" type="number" value={String(formData.style.paddingBottom)} onChange={(v) => updateStyleField('paddingBottom', parseInt(v) || 0)} autoComplete="off" suffix="px" />
            <TextField label="Left" type="number" value={String(formData.style.paddingLeft)} onChange={(v) => updateStyleField('paddingLeft', parseInt(v) || 0)} autoComplete="off" suffix="px" />
            <TextField label="Right" type="number" value={String(formData.style.paddingRight)} onChange={(v) => updateStyleField('paddingRight', parseInt(v) || 0)} autoComplete="off" suffix="px" />
          </InlineGrid>
        </BlockStack>

        <Divider />

        {/* Rotation */}
        <BlockStack gap="200">
          <Text as="p" variant="headingSm">Rotation</Text>
          <RangeSlider
            label="Rotation"
            labelHidden
            value={formData.rotation}
            onChange={(v) => updateField('rotation', v)}
            min={0} max={360} output suffix="°"
          />
        </BlockStack>
      </BlockStack>
    </Card>
  );

  /* ================================================================
     RENDER: POSITION TAB (9-Grid, Margins, CSS Selector)
     ================================================================ */
  const renderPositionTab = () => (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">Position</Text>

        {/* 9-Grid Position Selector */}
        <BlockStack gap="200">
          <Text as="p" variant="headingSm">Corner</Text>
          <Text as="p" variant="bodySm" tone="subdued">Choose your Label position</Text>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 60px)', gap: 6 }}>
            {POSITION_GRID.flat().map((pos) => (
              <div
                key={pos}
                onClick={() => {
                  updatePosition('corner', pos);
                  updateField('position', pos);
                }}
                style={{
                  width: 60, height: 60,
                  border: formData.advancedPosition.corner === pos ? '2px solid #2C6ECB' : '1px solid #ccc',
                  borderRadius: 6, cursor: 'pointer', position: 'relative',
                  backgroundColor: formData.advancedPosition.corner === pos ? '#F1F8FF' : '#fff',
                }}
              >
                <div
                  style={{
                    width: 10, height: 10, borderRadius: '50%',
                    backgroundColor: formData.advancedPosition.corner === pos ? '#2C6ECB' : '#aaa',
                    position: 'absolute',
                    ...(pos.includes('top') ? { top: 6 } : pos.includes('bottom') ? { bottom: 6 } : { top: '50%', transform: 'translateY(-50%)' }),
                    ...(pos.includes('left') ? { left: 6 } : pos.includes('right') ? { right: 6 } : { left: '50%', transform: pos.includes('middle') || pos === 'center' ? 'translate(-50%, -50%)' : 'translateX(-50%)' }),
                    ...(pos === 'center' ? { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' } : {}),
                  }}
                />
              </div>
            ))}
          </div>
        </BlockStack>

        <Divider />

        {/* Margin Controls */}
        <BlockStack gap="300">
          <Text as="p" variant="headingSm">Margin</Text>
          <InlineGrid columns={2} gap="200">
            <TextField label="Top" type="number" value={String(formData.advancedPosition.marginTop)} onChange={(v) => updatePosition('marginTop', parseInt(v) || 0)} autoComplete="off" suffix="px" />
            <TextField label="Bottom" type="number" value={String(formData.advancedPosition.marginBottom)} onChange={(v) => updatePosition('marginBottom', parseInt(v) || 0)} autoComplete="off" suffix="px" />
            <TextField label="Left" type="number" value={String(formData.advancedPosition.marginLeft)} onChange={(v) => updatePosition('marginLeft', parseInt(v) || 0)} autoComplete="off" suffix="px" />
            <TextField label="Right" type="number" value={String(formData.advancedPosition.marginRight)} onChange={(v) => updatePosition('marginRight', parseInt(v) || 0)} autoComplete="off" suffix="px" />
          </InlineGrid>
          <Select
            label="Unit"
            options={[
              { label: 'px', value: 'px' },
              { label: '%', value: '%' },
              { label: 'em', value: 'em' },
              { label: 'rem', value: 'rem' },
            ]}
            value={formData.advancedPosition.marginUnit}
            onChange={(v) => updatePosition('marginUnit', v)}
          />
        </BlockStack>

        <Divider />

        {/* Custom CSS Selector */}
        <TextField
          label="Custom CSS selector"
          value={formData.advancedPosition.customCSSSelector}
          onChange={(v) => updatePosition('customCSSSelector', v)}
          autoComplete="off"
          placeholder=".product-card__image-wrapper, .cart-item__image"
          helpText="Target specific elements on the page for label placement"
        />
      </BlockStack>
    </Card>
  );

  /* ================================================================
     RENDER: CONDITIONS TAB (Advanced Display Conditions)
     ================================================================ */
  const renderConditionsTab = () => (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">Display Conditions</Text>

        {conditionsLoadError && (
          <Banner
            title="Could not load store data"
            tone="warning"
            onDismiss={() => setConditionsLoadError(null)}
          >
            <p>{conditionsLoadError}</p>
            <p>Try refreshing the page. If the issue persists, reinstall the app from the Shopify App Store.</p>
          </Banner>
        )}

        {/* ANY / ALL Radio */}
        <BlockStack gap="200">
          <Text as="p" variant="bodySm" fontWeight="semibold">Products must match:</Text>
          <InlineStack gap="400">
            <RadioButton
              label="any condition"
              checked={formData.matchType === 'any'}
              id="match-any"
              name="matchType"
              onChange={() => updateField('matchType', 'any')}
            />
            <RadioButton
              label="all conditions"
              checked={formData.matchType === 'all'}
              id="match-all"
              name="matchType"
              onChange={() => updateField('matchType', 'all')}
            />
          </InlineStack>
        </BlockStack>

        <Divider />

        {/* Condition rows: only show when there are valid conditions (no default/placeholder field) */}
        {formData.conditions.advancedConditions.length === 0 ? (
          <BlockStack gap="200">
            <div style={{ padding: '24px 16px', textAlign: 'center', backgroundColor: 'var(--p-color-bg-surface-secondary)', borderRadius: '8px' }}>
              <Text as="p" variant="bodyMd" tone="subdued">
                No conditions added yet. Click Add condition to create one.
              </Text>
            </div>
          </BlockStack>
        ) : (
          formData.conditions.advancedConditions.map((cond, idx) => (
            <Card key={idx}>
              <InlineStack align="space-between" blockAlign="start">
                <BlockStack gap="200" inlineAlign="stretch">
                  <Text as="p" variant="bodySm" fontWeight="semibold">
                    {CONDITION_TYPES.find((t) => t.value === cond.type)?.label || cond.type}
                  </Text>
                  {renderConditionEditor(cond, idx)}
                </BlockStack>
                <Button icon={DeleteMinor} variant="plain" tone="critical" onClick={() => removeCondition(idx)} accessibilityLabel="Remove condition" />
              </InlineStack>
            </Card>
          ))
        )}

        {/* Add Condition Button */}
        <Popover
          active={conditionPopoverActive}
          activator={
            <Button icon={CirclePlusMinor} onClick={() => setConditionPopoverActive(true)}>
              Add condition
            </Button>
          }
          onClose={() => setConditionPopoverActive(false)}
        >
          <ActionList
            items={CONDITION_TYPES.map((ct) => ({
              content: ct.label,
              onAction: () => addCondition(ct.value),
            }))}
          />
        </Popover>
      </BlockStack>
    </Card>
  );

  /* ── Render editor for a single condition ── */
  const renderConditionEditor = (cond: Condition, idx: number) => {
    switch (cond.type) {
      case 'products_selected':
        return (
          <BlockStack gap="300">
            <Select label="Operator" options={[{ label: 'Is equal', value: 'is_equal' }, { label: 'Is not equal', value: 'is_not_equal' }]} value={cond.operator} onChange={(v) => updateCondition(idx, 'operator', v)} />
            <Button fullWidth onClick={() => openProductPicker(idx)}>
              Select products
            </Button>
            {cond.values && cond.values.length > 0 && (
              <BlockStack gap="200">
                <Text as="p" variant="bodySm" tone="subdued">
                  {cond.values.length} product{cond.values.length !== 1 ? 's' : ''} selected
                </Text>
                <InlineStack gap="200" wrap>
                  {cond.values.map((val: string) => {
                    const product = getProductById(val);
                    return (
                      <Tag
                        key={val}
                        onRemove={() => {
                          const newValues = cond.values.filter((v: string) => v !== val);
                          updateCondition(idx, 'values', newValues);
                        }}
                      >
                        {product ? product.title : val}
                      </Tag>
                    );
                  })}
                </InlineStack>
              </BlockStack>
            )}
          </BlockStack>
        );

      case 'collections_selected':
        return (
          <BlockStack gap="300">
            <Select label="Operator" options={[{ label: 'Is equal', value: 'is_equal' }, { label: 'Is not equal', value: 'is_not_equal' }]} value={cond.operator} onChange={(v) => updateCondition(idx, 'operator', v)} />
            <Button fullWidth onClick={() => openCollectionPicker(idx)}>
              Select collections
            </Button>
            {cond.values && cond.values.length > 0 && (
              <BlockStack gap="200">
                <Text as="p" variant="bodySm" tone="subdued">
                  {cond.values.length} collection{cond.values.length !== 1 ? 's' : ''} selected
                </Text>
                <InlineStack gap="200" wrap>
                  {cond.values.map((cid: string) => {
                    const col = getCollectionById(cid);
                    return (
                      <Tag
                        key={cid}
                        onRemove={() => {
                          const newValues = (cond.values || []).filter((v: string) => v !== cid);
                          updateCondition(idx, 'values', newValues);
                        }}
                      >
                        {col ? col.title : cid}
                      </Tag>
                    );
                  })}
                </InlineStack>
              </BlockStack>
            )}
          </BlockStack>
        );

      case 'product_tag':
        return (
          <BlockStack gap="200">
            <Select label="Operator" options={[{ label: 'Contains', value: 'contains' }, { label: 'Does not contain', value: 'not_contains' }]} value={cond.operator} onChange={(v) => updateCondition(idx, 'operator', v)} />
            <TextField label="Tag value" value={cond.value || ''} onChange={(v) => updateCondition(idx, 'value', v)} autoComplete="off" placeholder="e.g., sale" />
          </BlockStack>
        );

      case 'product_created':
        return (
          <BlockStack gap="200">
            <Select label="Operator" options={[{ label: 'Is after', value: 'is_after' }, { label: 'Is before', value: 'is_before' }, { label: 'Is between', value: 'is_between' }]} value={cond.operator} onChange={(v) => updateCondition(idx, 'operator', v)} />
            <TextField label="Date" type="date" value={cond.value || ''} onChange={(v) => updateCondition(idx, 'value', v)} autoComplete="off" />
            {cond.operator === 'is_between' && (
              <TextField label="End Date" type="date" value={cond.values?.[0] || ''} onChange={(v) => updateCondition(idx, 'values', [v])} autoComplete="off" />
            )}
          </BlockStack>
        );

      case 'product_title':
        return (
          <BlockStack gap="200">
            <Select label="Operator" options={[{ label: 'Contains', value: 'contains' }, { label: 'Starts with', value: 'starts_with' }, { label: 'Ends with', value: 'ends_with' }]} value={cond.operator} onChange={(v) => updateCondition(idx, 'operator', v)} />
            <TextField label="Title text" value={cond.value || ''} onChange={(v) => updateCondition(idx, 'value', v)} autoComplete="off" />
          </BlockStack>
        );

      case 'product_type':
      case 'product_vendor':
        return (
          <BlockStack gap="200">
            <Select label="Operator" options={[{ label: 'Is equal', value: 'is_equal' }, { label: 'Is not equal', value: 'is_not_equal' }]} value={cond.operator} onChange={(v) => updateCondition(idx, 'operator', v)} />
            <TextField label={cond.type === 'product_type' ? 'Product type' : 'Vendor name'} value={cond.value || ''} onChange={(v) => updateCondition(idx, 'value', v)} autoComplete="off" />
          </BlockStack>
        );

      case 'product_price':
      case 'product_compare_at_price':
      case 'product_weight':
        return (
          <BlockStack gap="200">
            <Select label="Operator" options={[
              { label: 'Is greater than', value: 'is_greater_than' },
              { label: 'Is less than', value: 'is_less_than' },
              { label: 'Is between', value: 'is_between' },
              ...(cond.type === 'product_compare_at_price' ? [{ label: 'Has value', value: 'has_value' }, { label: 'No value', value: 'no_value' }] : []),
            ]} value={cond.operator} onChange={(v) => updateCondition(idx, 'operator', v)} />
            {!['has_value', 'no_value'].includes(cond.operator) && (
              <TextField label="Value" type="number" value={cond.value?.toString() || ''} onChange={(v) => updateCondition(idx, 'value', parseFloat(v) || 0)} autoComplete="off" />
            )}
            {cond.operator === 'is_between' && (
              <TextField label="Max value" type="number" value={cond.values?.[0]?.toString() || ''} onChange={(v) => updateCondition(idx, 'values', [parseFloat(v) || 0])} autoComplete="off" />
            )}
          </BlockStack>
        );

      case 'product_published':
      case 'available_for_sale':
        return (
          <Select label="Status" options={[{ label: 'Is true', value: 'is_true' }, { label: 'Is false', value: 'is_false' }]} value={cond.operator} onChange={(v) => updateCondition(idx, 'operator', v)} />
        );

      case 'product_inventory':
        return (
          <BlockStack gap="200">
            <Select label="Operator" options={[
              { label: 'Is greater than', value: 'is_greater_than' },
              { label: 'Is less than', value: 'is_less_than' },
              { label: 'Is equal', value: 'is_equal' },
              { label: 'Is out of stock', value: 'is_out_of_stock' },
            ]} value={cond.operator} onChange={(v) => updateCondition(idx, 'operator', v)} />
            {cond.operator !== 'is_out_of_stock' && (
              <TextField label="Quantity" type="number" value={cond.value?.toString() || ''} onChange={(v) => updateCondition(idx, 'value', parseInt(v) || 0)} autoComplete="off" />
            )}
          </BlockStack>
        );

      case 'product_variants':
        return (
          <BlockStack gap="200">
            <Select label="Operator" options={[
              { label: 'Has variants', value: 'has_variants' },
              { label: 'No variants', value: 'no_variants' },
              { label: 'Variant count equals', value: 'variant_count_equal' },
            ]} value={cond.operator} onChange={(v) => updateCondition(idx, 'operator', v)} />
            {cond.operator === 'variant_count_equal' && (
              <TextField label="Count" type="number" value={cond.value?.toString() || ''} onChange={(v) => updateCondition(idx, 'value', parseInt(v) || 0)} autoComplete="off" />
            )}
          </BlockStack>
        );

      case 'visitor_country':
        return (
          <ChoiceList
            title="Select countries"
            allowMultiple
            choices={COUNTRY_OPTIONS}
            selected={cond.values || []}
            onChange={(v) => updateCondition(idx, 'values', v)}
          />
        );

      default:
        return (
          <TextField label="Value" value={cond.value || ''} onChange={(v) => updateCondition(idx, 'value', v)} autoComplete="off" />
        );
    }
  };

  /* ================================================================
     RENDER: VISIBILITY TAB (Page Visibility + Display Period)
     ================================================================ */
  const renderVisibilityTab = () => (
    <BlockStack gap="400">
      {/* Visibility on Pages */}
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">Visibility on pages</Text>
          <Text as="p" variant="bodySm" tone="subdued">Choose on which page to display your Label</Text>
          <BlockStack gap="200">
            <Checkbox label="Home Page" checked={formData.visibility.homePage} onChange={(v) => updateVisibility('homePage', v)} />
            <Checkbox label="Product Pages" checked={formData.visibility.productPages} onChange={(v) => updateVisibility('productPages', v)} />
            <Checkbox label="Search Results Pages" checked={formData.visibility.searchResultsPages} onChange={(v) => updateVisibility('searchResultsPages', v)} />
            <Checkbox label="Cart Page" checked={formData.visibility.cartPage} onChange={(v) => updateVisibility('cartPage', v)} />
            <Checkbox label="Collection Pages" checked={formData.visibility.collectionPages} onChange={(v) => updateVisibility('collectionPages', v)} />
            <Checkbox label="Other Pages" checked={formData.visibility.otherPages} onChange={(v) => updateVisibility('otherPages', v)} />
          </BlockStack>
        </BlockStack>
      </Card>

      {/* Display Period */}
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">Display period</Text>
          <Checkbox
            label="All time"
            checked={formData.displayPeriod.allTime}
            onChange={(v) => updateDisplayPeriod('allTime', v)}
          />
          {!formData.displayPeriod.allTime && (
            <BlockStack gap="200">
              <Text as="p" variant="bodySm" tone="subdued">Setup display of the day range</Text>
              <InlineStack gap="300">
                <TextField
                  label="Start Date"
                  type="date"
                  value={formData.displayPeriod.startDate}
                  onChange={(v) => updateDisplayPeriod('startDate', v)}
                  autoComplete="off"
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={formData.displayPeriod.endDate}
                  onChange={(v) => updateDisplayPeriod('endDate', v)}
                  autoComplete="off"
                />
              </InlineStack>
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </BlockStack>
  );

  /* ================================================================
     RENDER: SCHEDULE TAB (Week Schedule)
     ================================================================ */
  const renderScheduleTab = () => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    return (
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">Week schedule</Text>
          <Text as="p" variant="bodySm" tone="subdued">
            Setup display of the week schedule
          </Text>

          <Select
            label="Timezone"
            options={TIMEZONE_OPTIONS}
            value={formData.weekSchedule.timezone}
            onChange={(v) => setFormData((prev) => ({
              ...prev,
              weekSchedule: { ...prev.weekSchedule, timezone: v },
            }))}
          />

          <Divider />

          {dayNames.map((day, i) => {
            const schedule = (formData.weekSchedule.days as any)[day] as DaySchedule;
            return (
              <BlockStack gap="200" key={day}>
                <InlineStack align="space-between">
                  <Checkbox
                    label={dayLabels[i]}
                    checked={schedule.enabled}
                    onChange={(v) => updateWeekDay(day, 'enabled', v)}
                  />
                  <Checkbox
                    label="All day"
                    checked={schedule.allDay}
                    onChange={(v) => updateWeekDay(day, 'allDay', v)}
                  />
                </InlineStack>
                {schedule.enabled && !schedule.allDay && (
                  <InlineStack gap="200">
                    <Select
                      label="Start"
                      labelHidden
                      options={TIME_OPTIONS}
                      value={schedule.startTime}
                      onChange={(v) => updateWeekDay(day, 'startTime', v)}
                    />
                    <Select
                      label="End"
                      labelHidden
                      options={TIME_OPTIONS}
                      value={schedule.endTime}
                      onChange={(v) => updateWeekDay(day, 'endTime', v)}
                    />
                  </InlineStack>
                )}
                {i < dayNames.length - 1 && <Divider />}
              </BlockStack>
            );
          })}
        </BlockStack>
      </Card>
    );
  };

  /* ================================================================
     RENDER: LINK TAB
     ================================================================ */
  const renderLinkTab = () => (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">Link</Text>
        <Text as="p" variant="bodySm" tone="subdued">Enter the link on your Label</Text>
        <TextField
          label="URL"
          labelHidden
          value={formData.linkUrl}
          onChange={(v) => updateField('linkUrl', v)}
          autoComplete="off"
          placeholder="e.g. https://mysite.com"
          type="url"
        />
        <Checkbox
          label="Open in new window"
          checked={formData.linkNewWindow}
          onChange={(v) => updateField('linkNewWindow', v)}
        />
      </BlockStack>
    </Card>
  );

  /* ================================================================
     RENDER: ANIMATIONS TAB
     ================================================================ */
  const renderAnimationsTab = () => (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">Animations</Text>
        <Select
          label="Animation type"
          options={ANIMATION_OPTIONS}
          value={formData.animation}
          onChange={(v) => updateField('animation', v)}
        />
        {formData.animation !== 'none' && (
          <BlockStack gap="200">
            <Text as="p" variant="bodySm" tone="subdued">Preview:</Text>
            <div style={{ padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8, textAlign: 'center' }}>
              <div
                key={formData.animation}
                style={{
                  display: 'inline-block',
                  backgroundColor: formData.style.backgroundColor,
                  color: formData.style.textColor,
                  fontSize: `${formData.style.fontSize}px`,
                  fontFamily: formData.style.fontFamily,
                  borderRadius: `${formData.style.borderRadius}px`,
                  padding: `${formData.style.paddingTop}px ${formData.style.paddingRight}px ${formData.style.paddingBottom}px ${formData.style.paddingLeft}px`,
                  animation: formData.animation === 'pulse' ? 'plb-pulse 2s ease infinite' :
                    formData.animation === 'fade' ? 'plb-fade 1s ease' :
                    formData.animation === 'bounce_in' ? 'plb-bounce 1s ease' :
                    formData.animation === 'flash' ? 'plb-flash 1s ease infinite' : 'none',
                }}
              >
                {formData.text || 'Label Preview'}
              </div>
            </div>
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );

  /* ================================================================
     RENDER: TRANSLATION MODAL
     ================================================================ */
  const renderTranslationModal = () => (
    <Modal
      open={translationModalOpen}
      onClose={() => setTranslationModalOpen(false)}
      title="Translation for label"
      primaryAction={{ content: 'Apply', onAction: () => setTranslationModalOpen(false) }}
      secondaryActions={[{ content: 'Cancel', onAction: () => setTranslationModalOpen(false) }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {availableLocales.filter((l: any) => l.code !== 'en').map((locale: any) => (
            <BlockStack gap="200" key={locale.code}>
              <Text as="p" variant="headingSm">{locale.name}</Text>
              <InlineGrid columns={2} gap="300">
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" fontWeight="semibold">Original</Text>
                  <div style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: 4, minHeight: 60 }}>
                    <Text as="p" variant="bodyMd">{formData.text || '(empty)'}</Text>
                  </div>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" fontWeight="semibold">Translation</Text>
                  <TextField
                    label="" labelHidden
                    value={formData.translations[locale.code]?.labelText || ''}
                    onChange={(v) => updateTranslation(locale.code, v)}
                    autoComplete="off"
                    placeholder="Enter the translation for your label"
                    multiline={2}
                  />
                </BlockStack>
              </InlineGrid>
              <Divider />
            </BlockStack>
          ))}
          {availableLocales.filter((l: any) => l.code !== 'en').length === 0 && (
            <Text as="p" variant="bodySm" tone="subdued">
              No additional languages available. Languages can be configured in your Shopify store settings.
            </Text>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );

  /* ================================================================
     RENDER: PREVIEW PANEL
     ================================================================ */
  const renderPreview = () => {
    const s = formData.style;
    const previewStyle: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: s.backgroundColor,
      color: s.textColor,
      fontSize: `${s.fontSize}px`,
      fontFamily: s.fontFamily,
      fontWeight: s.fontWeight as any,
      fontStyle: s.fontStyle,
      textDecoration: s.textDecoration,
      borderRadius: formData.shape === 'circle' ? '50%' :
        formData.shape === 'pill' ? '999px' :
        formData.shape === 'rounded' ? '12px' :
        `${s.borderRadius}px`,
      padding: `${s.paddingTop}px ${s.paddingRight}px ${s.paddingBottom}px ${s.paddingLeft}px`,
      transform: formData.rotation ? `rotate(${formData.rotation}deg)` : undefined,
      whiteSpace: 'nowrap',
      lineHeight: 1.3,
      ...(s.width ? { width: s.width } : {}),
      ...(s.height ? { height: s.height } : {}),
    };

    return (
      <Card>
        <BlockStack gap="200">
          <Text as="h2" variant="headingMd">Preview</Text>
          <div style={{ padding: 24, backgroundColor: '#f5f5f5', borderRadius: 8, textAlign: 'center', minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {formData.type === 'image' && formData.imageUrl ? (
              <img src={formData.imageUrl} alt="Label" style={{ maxWidth: 150, maxHeight: 60, ...previewStyle, padding: 0, backgroundColor: 'transparent' }} />
            ) : (
              <div style={previewStyle}>
                {formData.text || 'Label Preview'}
              </div>
            )}
          </div>
        </BlockStack>
      </Card>
    );
  };

  /* ================================================================
     RENDER: MAIN PAGE
     ================================================================ */
  return (
    <Page
      title={id ? 'Edit Label' : 'Create Label'}
      backAction={{ onAction: () => navigate('/labels') }}
      primaryAction={{
        content: 'Save',
        onAction: handleSubmit,
        loading: saving,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: () => navigate('/labels'),
        },
      ]}
    >
      <Layout>
        {/* Left column: Editor */}
        <Layout.Section>
          <BlockStack gap="400">
            <Tabs tabs={tabs} selected={activeTab} onSelect={setActiveTab}>
              <div style={{ marginTop: 16 }}>
                {activeTab === 0 && renderLabelTab()}
                {activeTab === 1 && renderStylesTab()}
                {activeTab === 2 && renderPositionTab()}
                {activeTab === 3 && renderConditionsTab()}
                {activeTab === 4 && renderVisibilityTab()}
                {activeTab === 5 && renderScheduleTab()}
                {activeTab === 6 && renderLinkTab()}
                {activeTab === 7 && renderAnimationsTab()}
              </div>
            </Tabs>
          </BlockStack>
        </Layout.Section>

        {/* Right column: Preview */}
        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            {renderPreview()}

            {/* Quick info card */}
            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Customization</Text>
                <BlockStack gap="100">
                  {[
                    { icon: '🏷️', label: 'Label', tab: 0 },
                    { icon: '🎨', label: 'Styles', tab: 1 },
                    { icon: '📍', label: 'Position', tab: 2 },
                    { icon: '🔗', label: 'Link', tab: 6 },
                    { icon: '✨', label: 'Animations', tab: 7 },
                    { icon: '📋', label: 'Conditions', tab: 3 },
                    { icon: '📄', label: 'Visibility', tab: 4 },
                    { icon: '📅', label: 'Schedule', tab: 5 },
                  ].map((item) => (
                    <Button
                      key={item.tab}
                      variant="plain"
                      textAlign="start"
                      onClick={() => setActiveTab(item.tab)}
                    >
                      {item.icon + ' ' + item.label}
                    </Button>
                  ))}
                </BlockStack>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>

      {/* Translation Modal */}
      {renderTranslationModal()}

      {/* Product Picker Modal */}
      <Modal
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        title="Select products"
        primaryAction={{
          content: `Select (${productPickerSelected.length})`,
          onAction: confirmProductPicker,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setProductPickerOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Search products"
              labelHidden
              value={productSearchQuery}
              onChange={(v) => {
                setProductSearchQuery(v);
                searchProducts(v);
              }}
              autoComplete="off"
              placeholder="Search products..."
              clearButton
              onClearButtonClick={() => {
                setProductSearchQuery('');
                searchProducts('');
              }}
            />
            {productSearchLoading ? (
              <InlineStack align="center">
                <Spinner size="small" />
              </InlineStack>
            ) : availableProducts.length === 0 ? (
              <Text as="p" variant="bodyMd" alignment="center" tone="subdued">
                No products found
              </Text>
            ) : (
              <BlockStack gap="0">
                {availableProducts.map((product) => {
                  const pid = String(product.id);
                  const isSelected = productPickerSelected.includes(pid);
                  return (
                    <div
                      key={pid}
                      onClick={() => toggleProductInPicker(pid)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #e1e3e5',
                        backgroundColor: isSelected ? '#f1f8ff' : 'transparent',
                      }}
                    >
                      <Checkbox
                        label=""
                        labelHidden
                        checked={isSelected}
                        onChange={() => toggleProductInPicker(pid)}
                      />
                      <Thumbnail
                        source={product.image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'}
                        alt={product.title}
                        size="small"
                      />
                      <BlockStack gap="0">
                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                          {product.title}
                        </Text>
                        {product.status === 'archived' && (
                          <Text as="span" variant="bodySm" tone="subdued">
                            Archived
                          </Text>
                        )}
                      </BlockStack>
                    </div>
                  );
                })}
              </BlockStack>
            )}
            {productPickerSelected.length > 0 && (
              <Text as="p" variant="bodySm" tone="subdued">
                {productPickerSelected.length} product{productPickerSelected.length !== 1 ? 's' : ''} selected
              </Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Collection Picker Modal — Shopify-style */}
      <Modal
        open={collectionPickerOpen}
        onClose={() => setCollectionPickerOpen(false)}
        title="Select collections"
        primaryAction={{
          content: 'Select',
          onAction: confirmCollectionPicker,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setCollectionPickerOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Search collections"
              labelHidden
              value={collectionSearchQuery}
              onChange={setCollectionSearchQuery}
              autoComplete="off"
              placeholder="Search collections..."
              clearButton
              onClearButtonClick={() => setCollectionSearchQuery('')}
            />
            {availableCollections.length === 0 ? (
              <Text as="p" variant="bodyMd" alignment="center" tone="subdued">
                No collections found
              </Text>
            ) : (
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {(collectionSearchQuery
                  ? availableCollections.filter(
                      (c) =>
                        (c.title || '')
                          .toLowerCase()
                          .includes(collectionSearchQuery.toLowerCase())
                    )
                  : availableCollections
                ).map((collection) => {
                  const cid = String(collection.id);
                  const isSelected = collectionPickerSelected.includes(cid);
                  const productCount = (collection as any).productsCount ?? (collection as any).products_count;
                  return (
                    <div
                      key={cid}
                      onClick={() => toggleCollectionInPicker(cid)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--p-color-border-secondary)',
                        backgroundColor: isSelected ? 'var(--p-color-bg-surface-selected)' : 'transparent',
                      }}
                    >
                      <Checkbox
                        label=""
                        labelHidden
                        checked={isSelected}
                        onChange={() => toggleCollectionInPicker(cid)}
                      />
                      <BlockStack gap="0">
                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                          {collection.title}
                        </Text>
                        {productCount != null && (
                          <Text as="span" variant="bodySm" tone="subdued">
                            {productCount} product{productCount !== 1 ? 's' : ''}
                          </Text>
                        )}
                      </BlockStack>
                    </div>
                  );
                })}
              </div>
            )}
            <Text as="p" variant="bodySm" tone="subdued">
              {collectionPickerSelected.length} collection{collectionPickerSelected.length !== 1 ? 's' : ''} selected
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Inject animation keyframes for preview */}
      <style>{`
        @keyframes plb-pulse { 0% { transform: scale(1); } 50% { transform: scale(1.06); } 100% { transform: scale(1); } }
        @keyframes plb-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes plb-bounce { 0%,20%,50%,80%,100% { transform: translateY(0); } 40% { transform: translateY(-8px); } 60% { transform: translateY(-4px); } }
        @keyframes plb-flash { 0%,50%,100% { opacity: 1; } 25%,75% { opacity: 0; } }
      `}</style>
    </Page>
  );
}
